import {
  action,
  makeObservable,
  observable,
  reaction,
  runInAction,
  toJS,
} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {Handle3dSecure} from '../../../utils/entity/card';
import type {
  CoreUserCardsAddParams,
  CoreUserCardsDeleteParams,
  CoreUserCardsListParams,
  CoreUserCardsPrepareParams,
  CoreUserCardsReadParams,
} from '../../types/core/methods';
import type {CardApiModel} from './types';

import {envManager} from '../../../utils/env';
import {isTestEnv} from '../../../utils/test-utils/isTestEnv';
import {apiRequest} from '../../utils/apiRequest';
import {EntityStore} from '../../utils/entityStore';

export class CardStore<
  T extends CardApiModel = CardApiModel,
> extends EntityStore<
  T,
  CoreUserCardsReadParams,
  CoreUserCardsAddParams,
  object,
  CoreUserCardsDeleteParams
> {
  cards: {[key: string]: T} = {};

  constructor(...args: any[]) {
    super(...args);

    makeObservable(this, {
      cards: observable.shallow,
      setCards: !isTestEnv ? action.bound : false,
    });

    reaction(
      () => this.creating,
      creating => {
        if (creating.success && creating.entity) {
          this.cards[creating.entity!._id] = toJS(creating.entity);
        }
      },
    );

    reaction(
      () => this.deleting,
      deleting => {
        if (deleting.success) {
          delete this.cards[deleting.entity!._id];
        }
      },
    );
  }

  async addCard({
    data,
    handle3dSecure,
    returnUrl,
  }: {
    data: {
      cvc: string;
      exp_month: string;
      exp_year: string;
      number: string;
    };
    handle3dSecure: Handle3dSecure;
    returnUrl: string;
  }) {
    let cardToDelete: null | T = null;

    try {
      const stripeCreateCardResponse = await fetch(
        'https://api.stripe.com/v1/tokens',
        {
          body: Object.keys(data)
            .map(key => `card[${key}]=${data[key]}`)
            .join('&'),
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${envManager.get('stripeKey')}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          method: 'POST',
        },
      );

      const stripeCreateCardResponseJson =
        (await stripeCreateCardResponse.json()) as {
          error?: any;
          id: string;
        };

      if (stripeCreateCardResponseJson.error) {
        throw stripeCreateCardResponseJson.error;
      }

      const {card, clientSecret} = await this.prepare();

      cardToDelete = card;

      const stripeConfirmIntentResponse = await fetch(
        `https://api.stripe.com/v1/setup_intents/${card.pm_id}/confirm`,
        {
          body: [
            'payment_method_data[type]=card',
            `payment_method_data[card][token]=${stripeCreateCardResponseJson.id}`,
            `client_secret=${clientSecret}`,
            `return_url=${encodeURIComponent(returnUrl)}`,
          ].join('&'),
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${envManager.get('stripeKey')}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          method: 'POST',
        },
      );

      const stripeConfirmIntentResponseJson =
        (await stripeConfirmIntentResponse.json()) as {
          error?: any;
          next_action?: {
            redirect_to_url: {
              url: string;
            };
          };
          status: string;
        };

      if (stripeConfirmIntentResponseJson.error) {
        throw stripeConfirmIntentResponseJson.error;
      }

      if (
        stripeConfirmIntentResponseJson.status === 'requires_action' &&
        stripeConfirmIntentResponseJson.next_action
      ) {
        await handle3dSecure(
          stripeConfirmIntentResponseJson.next_action.redirect_to_url.url,
          returnUrl,
          card._id,
        );
      }

      // this method triggers sync with stripe on the backend
      const fetchedCard = await this.fetch({_id: card._id});

      if (fetchedCard.type === 'card') {
        cardToDelete = null;

        runInAction(() => {
          this.creating = {
            ...this.creating,
            entity: fetchedCard,
            error: null,
            success: true,
          };
        });

        return fetchedCard;
      } else {
        throw {
          message: i18n.t('shared:message.add_card_error'),
        };
      }
    } finally {
      if (cardToDelete) {
        this.delete(cardToDelete);
      }
    }
  }

  async fetchCards() {
    if (envManager.get('isB2b')) {
      return [];
    }

    const {_items}: {_items: T[]} = await apiRequest<CoreUserCardsListParams>({
      method: 'user.cards.list',
      params: {
        query: [['type', '==', 'card']],
      },
    });

    this.setCards(_items);

    return _items;
  }

  async prepare() {
    const result = await apiRequest<CoreUserCardsPrepareParams>({
      method: 'user.cards.prepare',
    });

    return {
      card: result.card as T,
      clientSecret: result.client_secret as string,
    };
  }

  setCards(cards: T[]) {
    this.cards = {};

    cards.forEach(card => {
      this.cards[card._id] = card;
    });
  }
}

export const cardStore = new CardStore('card', {
  create: 'user.cards.add',
  delete: 'user.cards.delete',
  fetch: 'user.cards.read',
});
